'use client';

import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import HighlightListItem from '@/app/[locale]/(default-layout)/specials/_components/HighlightListItem';
import { HighlightItem } from '@/app/[locale]/(default-layout)/specials/_components/SpecialsHeroContent';

const DEFAULT_VISIBLE_COUNT = 6;

type HighlightsSectionProps = Readonly<{
    highlights: HighlightItem[];
    activeHighlightIndex: number;
    onHighlightSelect: (index: number) => void;
}>;

export default function HighlightsSection({
    highlights,
    activeHighlightIndex,
    onHighlightSelect
}: HighlightsSectionProps): React.JSX.Element {
    const t = useTranslations();
    const [isExpanded, setIsExpanded] = useState(false);

    const canExpand = highlights.length > DEFAULT_VISIBLE_COUNT;
    const visibleHighlights = isExpanded ? highlights : highlights.slice(0, DEFAULT_VISIBLE_COUNT);

    // Split into left and right columns for desktop 2-column grid
    const leftColumnItems: { highlight: HighlightItem; originalIndex: number }[] = [];
    const rightColumnItems: { highlight: HighlightItem; originalIndex: number }[] = [];
    visibleHighlights.forEach((highlight, i) => {
        const originalIndex = isExpanded ? i : i;
        if (i % 2 === 0) {
            leftColumnItems.push({ highlight, originalIndex });
        } else {
            rightColumnItems.push({ highlight, originalIndex });
        }
    });

    return (
        <div className="flex flex-col gap-24 w-full">
            <h2 className="text-headline-20 truncate w-full">{t('specials.our-highlights')}</h2>

            {/* Desktop: 2-column grid with vertical divider */}
            <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-x-24 gap-y-32">
                {/* Left column */}
                <div className="flex flex-col gap-32">
                    {leftColumnItems.map(({ highlight, originalIndex }) => (
                        <HighlightListItem
                            key={highlight.showing.id}
                            highlight={highlight}
                            isActiveInHero={originalIndex === activeHighlightIndex}
                            onClick={() => onHighlightSelect(originalIndex)}
                        />
                    ))}
                </div>

                {/* Vertical divider */}
                <div className="w-px self-stretch opacity-20 bg-[var(--on-background-color)]" />

                {/* Right column */}
                <div className="flex flex-col gap-32">
                    {rightColumnItems.map(({ highlight, originalIndex }) => (
                        <HighlightListItem
                            key={highlight.showing.id}
                            highlight={highlight}
                            isActiveInHero={originalIndex === activeHighlightIndex}
                            onClick={() => onHighlightSelect(originalIndex)}
                        />
                    ))}
                </div>
            </div>

            {/* Mobile: single column */}
            <div className="flex flex-col gap-32 lg:hidden">
                {visibleHighlights.map((highlight, index) => (
                    <HighlightListItem
                        key={highlight.showing.id}
                        highlight={highlight}
                        isActiveInHero={index === activeHighlightIndex}
                        onClick={() => onHighlightSelect(index)}
                    />
                ))}
            </div>

            {/* Show all / Show less toggle */}
            {canExpand && (
                <button
                    className={classnames(
                        'text-left text-body-bold-14 underline cursor-pointer w-fit text-[var(--primary-color)]'
                    )}
                    onClick={() => setIsExpanded(!isExpanded)}
                    type="button">
                    {isExpanded ? t('specials.show-less') : t('specials.show-all')}
                </button>
            )}
        </div>
    );
}
